import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, catchError, finalize, interval, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import moment from 'moment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  baseUrl: string = environment.protuneApiUrl;
  public loginEvent: EventEmitter<void>;
  public logoutEvent: EventEmitter<void>;
  private userInfo: IInfoResult | null = null;
  private refreshInFlight$: Observable<any> | null = null;
  private loggedOut = false;

  constructor(private http: HttpClient, private router: Router) {
    this.loginEvent = new EventEmitter();
    this.logoutEvent = new EventEmitter();
  }

  login(email: string, password: string) {
    this.loggedOut = false;
    this.clearSession();
    return this.http.post<any>(`${this.baseUrl}/login`, { email, password }, { headers: { skip: 'true' } })
      .pipe(
        tap(res => {

          this.setSession(res);
          this.loginEvent.emit();
        }),
        shareReplay());
  }

  getUserInfo(): Observable<IInfoResult> {
    if (this.userInfo != null) {
      return of(this.userInfo);
    }

    return this.http.get<any>(`${this.baseUrl}/manage/info`)
      .pipe(
        map(res => {
          this.userInfo = {
            firstName: res.firstName,
            lastName: res.lastName,
            email: res.email,
            subscriptionLevel: res.subscriptionLevel,
            subscriptionExpiration: res.subscriptionExpiration,
            aiEngineerSubscriptionExpiration: res.aiEngineerSubscriptionExpiration,
            IsAIEngineerSubscriptionCancelled: res.IsAIEngineerSubscriptionCancelled,
            claims: { }
          };

          for (let claim of res.claims) {
            this.userInfo.claims[claim.type] = claim.value;
          }
          return this.userInfo;

        }),
        // tap(infoResult => {
        //   localStorage.setItem(AuthConstants.SubscriptionLevel, infoResult.subscriptionLevel);
        //   localStorage.setItem(AuthConstants.SubscriptionExpiration, infoResult.subscriptionExpiration.toString());
        //   localStorage.setItem(AuthConstants.Email, infoResult.email);
        //   localStorage.setItem(AuthConstants.FirstName, infoResult.firstName);
        //   localStorage.setItem(AuthConstants.LastName, infoResult.lastName);
        //   localStorage.setItem(AuthConstants.AIEngineerSubscriptionExpiration, infoResult.aiEngineerSubscriptionExpiration?.toString());
        //   localStorage.setItem(AuthConstants.IsAIEngineerSubscriptionCancelled, infoResult.IsAIEngineerSubscriptionCancelled?.toString());
        //   localStorage.setItem(AuthConstants.Claims, JSON.stringify(infoResult.claims));
        // }),
        shareReplay());
  }

  register(firstName: string, lastName: string, email: string, password: string, subscriptionLevel: string|number, emailOptIn: boolean) {
    this.loggedOut = false;
    this.clearSession();
    return this.http.post<any>(`${this.baseUrl}/register`, { firstName, lastName, email, password, subscriptionLevel, emailOptIn }, { headers: { skip: 'true' } })
      .pipe(
        tap(res => {
          this.setSession(res);
          this.getUserInfo().subscribe({
            next: () => {
              this.loginEvent.emit();
            }
          });
        }),
        shareReplay()
      );
  }

  refreshToken() {
    if (!this.refreshInFlight$) {
      const refreshToken = localStorage.getItem(AuthConstants.RefreshToken);

      this.refreshInFlight$ = this.http.post<any>(`${this.baseUrl}/refresh`, { refreshToken }, { headers: { skip: 'true' } })
        .pipe(
          tap(res => {
            if (this.loggedOut) {
              // logout() ran while this refresh was in flight: don't resurrect the
              // session, and revoke the just-issued token server-side (best-effort).
              this.http.post(`${this.baseUrl}/logout`, { refreshToken: res.refreshToken }, { headers: { skip: 'true' } })
                .subscribe({ next: () => {}, error: () => {} });
              return;
            }

            this.setSession(res);
            this.getUserInfo().subscribe();
          }),
          finalize(() => this.refreshInFlight$ = null),
          shareReplay(1));
    }

    return this.refreshInFlight$;
  }

  checkAuth() {
    const check = interval(300000);

    return check.pipe(
      tap((x) => {
        console.log(x);
      }),
      switchMap(() => {
        return this.http.get(`${this.baseUrl}/manage/info`)
          .pipe(
            catchError(err => {
              console.log(err);
              throw new Error('Check Auth Error', err);
            })
          );
      })
    );

  }

  logout() {
    // Block any in-flight refresh from resurrecting the session (see refreshToken()).
    this.loggedOut = true;
    this.refreshInFlight$ = null;

    const refreshToken = localStorage.getItem(AuthConstants.RefreshToken);

    if (refreshToken) {
      this.http.post<any>(`${this.baseUrl}/logout`, { refreshToken }, { headers: { skip: 'true' } })
        .subscribe({ next: () => {}, error: () => {} });
    }

    this.clearSession();
    this.logoutEvent.emit();

    window.location.href = '/login';
    //this.router.navigateByUrl('/login');
  }

  accessToken() {
    if (this.isTokenExpired()) {
      //try refreshing token with
      return this.refreshToken()
        .pipe(
          tap((res: any) => this.setSession),
          map((res: IAuthResult) => res.accessToken),
          shareReplay(),
          catchError(err => {
            //if refreshing token fails, perform logout
            console.log('Authentication Error', err);
            this.logout();
            return of([])
          }));
    }
    else {
      return of(localStorage.getItem(AuthConstants.AccessToken));
    }
  }

  public isLoggedIn(): Observable<boolean> {
    if (!this.hasToken()) {
      this.clearSession();
      return of(false);
    }

    const expired = moment().isAfter(this.getExpiration());
    const hasRefreshToken = this.hasRefreshToken();

    if (hasRefreshToken && expired) {
      return this.refreshToken()
        .pipe(
          map(() => {
            return true
          }),
          catchError(err => {
            this.clearSession();
            return of(false);
          })
        );
    }
    else if (hasRefreshToken && !expired) {
      return of(true);
    }


    return of(false);
  }


  isTokenExpired() {
    return moment().isAfter(this.getExpiration());
  }

  getExpiration() {
    const expiration = localStorage.getItem(AuthConstants.ExpiresAt);
    const expiresAt = moment.utc(JSON.parse(expiration));

    return expiresAt.isValid() ? expiresAt : moment().add(-1, 'days');
  }

  hasRefreshToken() {
    const refreshToken = localStorage.getItem(AuthConstants.RefreshToken)
    return refreshToken != null && refreshToken != '';
  }

  hasToken() {
    const token = localStorage.getItem(AuthConstants.AccessToken);
    return token != null && token != '';
  }

  // getEmail() {
  //   const email = localStorage.getItem(AuthConstants.Email);
  //   return email;
  // }

  // getFirstName() {
  //   const firstName = localStorage.getItem(AuthConstants.FirstName);
  //   return firstName;
  // }

  // getLastName() {
  //   const lastName = localStorage.getItem(AuthConstants.LastName);
  //   return lastName;
  // }

  // getClaims() {
  //   const serverClaims = JSON.parse(localStorage.getItem(AuthConstants.Claims));
  //   let claims: { [key: string]: string } = { };

  //   for (let claim of serverClaims) {
  //     claims[claim.type] = claim.value;
  //   }
    

  //   return claims
  // }

  getClaims() {
    const claims = JSON.parse(localStorage.getItem(AuthConstants.Claims)) as Claims;
    return claims
  }

  getSubscription(forceUpdate: boolean = false) {
    let subscription: AppSubscription = new AppSubscription();

    if (!forceUpdate && localStorage.hasOwnProperty(AuthConstants.SubscriptionExpiration) && localStorage.hasOwnProperty(AuthConstants.SubscriptionLevel)) {
      const expirationDate = new Date(localStorage.getItem(AuthConstants.SubscriptionExpiration));
      const aiExpirationDate = new Date(localStorage.getItem(AuthConstants.AIEngineerSubscriptionExpiration));

      subscription.subscriptionLevel = localStorage.getItem(AuthConstants.SubscriptionLevel);
      subscription.subscriptionExpiration = expirationDate;
      subscription.aiEngineerSubscriptionExpiration = !isNaN(aiExpirationDate.getTime()) ? aiExpirationDate : null;
      subscription.isAIEngineerSubscriptionCancelled = (localStorage.getItem(AuthConstants.IsAIEngineerSubscriptionCancelled) ?? 'false') == 'true';

      return of(subscription);
    }
    else {
      return this.getUserInfo()
        .pipe(
          map(infoResult => {
            const aiExpirationDate = new Date(infoResult.aiEngineerSubscriptionExpiration);
            subscription.subscriptionExpiration = infoResult.subscriptionExpiration;
            subscription.subscriptionLevel = infoResult.subscriptionLevel;
            subscription.aiEngineerSubscriptionExpiration = !isNaN(aiExpirationDate.getTime()) ? aiExpirationDate : null;

            return subscription;
          }));
    }

  }

  forgotPassword(email: string) {
    return this.http.post(`${this.baseUrl}/forgotpassword`, { email }, { headers: { skip: 'true' } });
  }

  resetPasswordWithCode(email: string, newPassword: string, resetCode: string) {
    return this.http.post(`${this.baseUrl}/resetpassword`, { email, newPassword, resetCode }, { headers: { skip: 'true' } });
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post<any>(`${this.baseUrl}/user/changePassword`, { currentPassword, newPassword })
      .pipe(
        tap(res => {

          this.setSession(res);
        }),
        map(res => {
          return null;
        }));
  }

  // isAdmin() {
  //   let claims = this.getClaims();
  //   return claims.Administrator == 'true';
  // }

  private setSession(authResult: IAuthResult) {
    const expiresAt = moment().add(authResult.expiresIn, 'seconds');

    localStorage.setItem(AuthConstants.AccessToken, authResult.accessToken);
    localStorage.setItem(AuthConstants.ExpiresAt, JSON.stringify(expiresAt));
    localStorage.setItem(AuthConstants.RefreshToken, authResult.refreshToken);
  }

  private clearSession() {
    localStorage.removeItem(AuthConstants.AccessToken);
    localStorage.removeItem(AuthConstants.ExpiresAt);
    localStorage.removeItem(AuthConstants.RefreshToken);
    localStorage.removeItem(AuthConstants.SubscriptionLevel);
    localStorage.removeItem(AuthConstants.SubscriptionExpiration);
    localStorage.removeItem(AuthConstants.AIEngineerSubscriptionExpiration);
    localStorage.removeItem('carTrackSettings');
    localStorage.removeItem('drivetrainSettings');
    localStorage.removeItem('suspensionSettings');
    localStorage.removeItem(AuthConstants.Claims);
    this.userInfo = null;
  }
}


interface IAuthResult {
  tokenType: string;
  accessToken: string;
  expiresIn: string;
  refreshToken: string;
}

interface IInfoResult {
  firstName: string;
  lastName: string;
  email: string,
  subscriptionLevel: string;
  subscriptionExpiration: Date;
  aiEngineerSubscriptionExpiration?: Date;
  IsAIEngineerSubscriptionCancelled: boolean;
  claims: { [key: string]: string };
}

export class AppSubscription {
  subscriptionLevel: string | number;
  subscriptionExpiration?: Date;
  isActive: boolean;
  aiEngineerSubscriptionExpiration?: Date;
  isAIEngineerSubscriptionCancelled: boolean;  
}

export class AuthConstants {
  static AccessToken: string = 'access_token';
  static ExpiresAt: string = 'expires_at';
  static RefreshToken: string = 'refresh_token';
  static SubscriptionLevel: string = 'subscriptionLevel';
  static SubscriptionExpiration: string = 'subscriptionExpiration';
  static Email: string = 'email';
  static FirstName: string = 'firstName';
  static LastName: string = 'lastName';
  static AIEngineerSubscriptionExpiration: string = "aiEngineerSubscriptionExpiration";
  static IsAIEngineerSubscriptionCancelled: string = "isAIEngineerSubscriptionCancelled"
  static Claims: string = 'claims';
}

export class Claims {
  Administrator: string;
}
